import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  console.log("AuthProvider rendering. Current user state:", user ? "logged in" : "not logged in");

  useEffect(() => {
    // Check for existing session
    const getSession = async () => {
      try {
        console.log("Checking for existing auth session...");
        
        // First check localStorage for admin user (for persistent login)
        const storedAdmin = localStorage.getItem('adminUser');
        if (storedAdmin) {
          console.log("Found stored admin user");
          try {
            const parsedUser = JSON.parse(storedAdmin);
            setUser(parsedUser);
            console.log("Using stored admin user:", parsedUser);
            setLoading(false);
            return; // Exit early if we found a stored admin
          } catch (error) {
            console.error("Error parsing stored admin user:", error);
            localStorage.removeItem('adminUser');
          }
        }
        
        // If no stored admin, try Supabase session
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          console.log("Found Supabase session, setting user");
          setUser(session.user);
        } else {
          console.log("No active session found");
          setUser(null);
        }
      } catch (error) {
        console.error("Auth session error:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("Auth state changed, session:", !!session);
      if (session?.user) {
        setUser(session.user);
      }
      // Don't clear user here if session is null, as we might be using localStorage admin
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    console.log("Login function called with email:", email);
    try {
      // If we're using the hardcoded admin credentials
      if (email === 'admin@minecraft-education.com' && password === '1234@Mrd') {
        console.log("Using hardcoded admin credentials");
        // Create a mock user for the hardcoded admin
        const mockUser = {
          id: 'admin-user',
          email: 'admin@minecraft-education.com',
          user_metadata: {
            role: 'admin'
          }
        };
        
        // Store in localStorage to maintain session
        localStorage.setItem('adminUser', JSON.stringify(mockUser));
        
        // Update state
        setUser(mockUser);
        console.log("Admin user set in state:", mockUser);
        
        return { user: mockUser };
      }
      
      // If not using hardcoded credentials, try Supabase authentication
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.log("Supabase auth error:", error);
        throw error;
      } 
      
      if (data?.user) {
        // If we got here, auth was successful with Supabase
        console.log("Supabase auth successful");
        setUser(data.user);
        return data;
      }

      throw new Error('Invalid login credentials');
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = async () => {
    console.log("Logout function called");
    try {
      // Try to sign out with Supabase
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Always clear local state
      setUser(null);
      localStorage.removeItem('adminUser');
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};